import { cn } from '@savvy-kit/lib/utils';
import { UserRound } from 'lucide-react';

import type { AvatarProps as AvatarContainerProps } from './avatar-primitive';
import { Avatar as AvatarContainer, AvatarFallback, AvatarImage } from './avatar-primitive';

export { Avatar, AvatarBadge, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage } from './avatar-primitive';

type PrimitiveSize = 'sm' | 'default' | 'lg';
type OverrideSize = 'xs' | 'xl' | '2xl';
type WrapperSize = PrimitiveSize | OverrideSize;

// Sizes the underlying base-ui primitive renders directly (via data-size).
const PRIMITIVE_SIZES: ReadonlySet<WrapperSize> = new Set<PrimitiveSize>(['sm', 'default', 'lg']);

function isPrimitiveSize(size: WrapperSize): size is PrimitiveSize {
  return PRIMITIVE_SIZES.has(size);
}

// Wrapper-only sizes — passed through as className overrides on top of the primitive.
const SIZE_CLASS_OVERRIDES: Record<OverrideSize, string> = {
  xs: 'size-5',
  xl: 'size-12',
  '2xl': 'size-14',
};

export type AvatarProps = Omit<AvatarContainerProps, 'size'> & {
  readonly size?: WrapperSize;
  readonly imageUrl?: string | null;
  readonly firstName?: string;
  readonly lastName?: string | null;
  readonly showFallbackIcon?: boolean;
  readonly customFallback?: React.ReactNode;
};

function Avatar({
  imageUrl,
  firstName,
  lastName,
  size = 'default',
  showFallbackIcon,
  customFallback,
  className,
}: AvatarProps) {
  const initials = `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase();
  const primitiveSize = isPrimitiveSize(size) ? size : 'default';
  const overrideClass = isPrimitiveSize(size) ? undefined : SIZE_CLASS_OVERRIDES[size];

  return (
    <AvatarContainer size={primitiveSize} className={cn(overrideClass, className)}>
      <AvatarImage src={imageUrl ?? ''} alt="user-avatar" />
      {customFallback ? (
        customFallback
      ) : (
        <AvatarFallback className={cn(size === 'xs' && 'text-2xs')}>
          {showFallbackIcon ? <UserRound className="text-dark" /> : initials}
        </AvatarFallback>
      )}
    </AvatarContainer>
  );
}

export default Avatar;
